import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { fetchCoupons, createCoupon, deleteCoupon } from "../store/actions/couponActions";
import { fetchStores } from "../store/actions/storeActions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import {
  Tag,
  Search,
  Download,
  Percent,
  Store,
  Calendar,
  BarChart,
  MoreVertical,
} from "lucide-react";

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 24px;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Coupons = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { coupons, loading, error } = useSelector((state) => state.coupon);
  const { stores = [] } = useSelector((state) => state.store);
  const [tab, setTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterStoreId, setFilterStoreId] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newType, setNewType] = useState("percentage");
  const [newAmount, setNewAmount] = useState("");
  const [newLimit, setNewLimit] = useState("");
  const [openActionsId, setOpenActionsId] = useState(null);
  const actionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setOpenActionsId(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(fetchStores());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCoupons({ storeId: filterStoreId || undefined }));
  }, [dispatch, filterStoreId]);

  const formatAmount = (coupon) => {
    const type = coupon.type || coupon.discountType || "percentage";
    const amount = coupon.amount ?? coupon.discountValue ?? 0;
    return type === "percentage" || type === "percent" ? `${amount}%` : `$${amount}`;
  };

  const formatMinMaxSpend = (coupon) => {
    const min = coupon.minimumSpend ?? coupon.minSpend;
    const max = coupon.maximumSpend ?? coupon.maxSpend;
    if (min == null && max == null) return "–";
    if (min != null && max != null) return `$${min} – $${max}`;
    if (min != null) return `$${min}+`;
    return `Up to $${max}`;
  };

  const filteredCoupons = (coupons || []).filter((coupon) => {
    const matchSearch = (coupon.code || "").toLowerCase().includes(searchQuery.toLowerCase());
    const status = (coupon.status ?? (coupon.isActive ? "active" : "inactive")).toLowerCase();
    const matchStatus = statusFilter === "all" || status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    const amount = Number(newAmount);
    if (!newCode.trim() || isNaN(amount)) return;
    const payload = {
      code: newCode.trim(),
      discountType: newType === "percentage" ? "percent" : "fixed",
      discountValue: amount,
    };
    if (newLimit.trim() && !isNaN(Number(newLimit))) payload.usageLimit = Number(newLimit);
    const result = await dispatch(createCoupon(payload));
    if (result?.success) {
      setShowCreate(false);
      setNewCode("");
      setNewAmount("");
      setNewLimit("");
    }
  };

  const list = coupons || [];
  const now = new Date();
  const in14Days = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
  const totalRedemptions = list.reduce((s, c) => s + (c.usageCount ?? c.usedCount ?? 0), 0);
  const percentType = list.filter((c) => (c.discountType || c.type) === "percent" || (c.discountType || c.type) === "percentage").length;
  const fixedType = list.filter((c) => (c.discountType || c.type) === "fixed").length;
  const activeCount = list.filter((c) => {
    const to = c.validTo || c.expiresAt || c.expiryDate;
    return !to || new Date(to) >= now;
  }).length;
  const expiringSoonCount = list.filter((c) => {
    const to = c.validTo || c.expiresAt || c.expiryDate;
    if (!to) return false;
    const d = new Date(to);
    return d >= now && d <= in14Days;
  }).length;
  const atLimitCount = list.filter((c) => {
    const limit = c.usageLimit ?? c.maxUsage;
    const used = c.usageCount ?? c.usedCount ?? 0;
    return limit != null && limit > 0 && used >= limit;
  }).length;
  const mostUsed = [...list].sort((a, b) => (b.usageCount ?? b.usedCount ?? 0) - (a.usageCount ?? a.usedCount ?? 0)).slice(0, 6);
  const expiringSoon = list.filter((c) => {
    const to = c.validTo || c.expiresAt || c.expiryDate;
    if (!to) return false;
    const d = new Date(to);
    return d >= now && d <= in14Days;
  }).sort((a, b) => new Date(a.validTo || a.expiresAt) - new Date(b.validTo || b.expiresAt)).slice(0, 6);
  const recentlyCreated = [...list].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 6);

  const stats = [
    { nameKey: "coupons.totalCoupons", value: String(list.length), icon: Tag },
    { nameKey: "coupons.totalRedemptions", value: String(totalRedemptions), icon: Percent },
    { nameKey: "coupons.percentType", value: String(percentType), icon: Percent },
    { nameKey: "coupons.fixedType", value: String(fixedType), icon: Tag },
    { nameKey: "coupons.active", value: String(activeCount), icon: Store },
    { nameKey: "coupons.expiringSoon", value: String(expiringSoonCount), icon: Calendar },
    { nameKey: "coupons.atLimit", value: String(atLimitCount), icon: BarChart },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title={t("coupons.title")}
        sub={t("coupons.subtitle")}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("coupons.overview")}</TabsTrigger>
          <TabsTrigger value="coupons">{t("coupons.allCoupons")}</TabsTrigger>
          <TabsTrigger value="analytics">{t("coupons.analytics")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }}>
            <StatsGrid>
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card key={stat.nameKey} style={{ padding: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <p style={{ fontSize: "14px", fontWeight: 500, color: "#6b7280", margin: 0 }}>{t(stat.nameKey)}</p>
                        <p style={{ margin: "8px 0 0", fontSize: "30px", fontWeight: 600, color: "#111827" }}>{stat.value}</p>
                      </div>
                      <div style={{ borderRadius: "9999px", padding: "12px", background: "rgba(26, 31, 44, 0.1)" }}>
                        <Icon style={{ height: "24px", width: "24px", color: "#1A1F2C" }} />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </StatsGrid>

            <OverviewGrid>
              <Card style={{ padding: "20px" }}>
                <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("coupons.mostUsed")}</h2>
                {mostUsed.length > 0 ? (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {mostUsed.map((c) => (
                      <li key={c._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 500 }}>{c.code}</span>
                        <span style={{ color: "#6b7280", fontSize: 14 }}>{c.usageCount ?? c.usedCount ?? 0} {t("coupons.uses")}{c.usageLimit ? ` / ${c.usageLimit}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("coupons.noUsageData")}</p>
                )}
              </Card>
              <Card style={{ padding: "20px" }}>
                <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("coupons.expiringSoonTitle")}</h2>
                {expiringSoon.length > 0 ? (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {expiringSoon.map((c) => (
                      <li key={c._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 500 }}>{c.code}</span>
                        <span style={{ color: "#b45309", fontSize: 13 }}>{c.validTo || c.expiresAt ? new Date(c.validTo || c.expiresAt).toLocaleDateString() : "—"}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("coupons.noExpiringSoon")}</p>
                )}
              </Card>
              <Card style={{ padding: "20px" }}>
                <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("coupons.recentlyCreated")}</h2>
                {recentlyCreated.length > 0 ? (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {recentlyCreated.map((c) => (
                      <li key={c._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 500 }}>{c.code}</span>
                        <span style={{ color: "#6b7280", fontSize: 13 }}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("coupons.noCouponsYet")}</p>
                )}
              </Card>
            </OverviewGrid>

            <Card style={{ padding: "20px", marginTop: "24px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("coupons.quickActions")}</h2>
              {showCreate ? (
                <form onSubmit={handleCreateCoupon} style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "320px", marginBottom: "16px" }}>
                  <Input placeholder={t("coupons.codePlaceholder")} value={newCode} onChange={(e) => setNewCode(e.target.value)} />
                  <select value={newType} onChange={(e) => setNewType(e.target.value)} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db" }}>
                    <option value="percentage">{t("coupons.percentage")}</option>
                    <option value="fixed">{t("coupons.fixedAmount")}</option>
                  </select>
                  <Input type="number" placeholder={t("coupons.amount")} value={newAmount} onChange={(e) => setNewAmount(e.target.value)} />
                  <Input type="number" placeholder={t("coupons.usageLimitPlaceholder")} value={newLimit} onChange={(e) => setNewLimit(e.target.value)} />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button type="submit" disabled={loading}>{t("coupons.create")}</Button>
                    <Button type="button" $variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
                  </div>
                </form>
              ) : null}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                <Button onClick={() => setShowCreate(!showCreate)}>
                  <Tag size={16} />
                  {t("coupons.createNewCoupon")}
                </Button>
                <Button $variant="outline">
                  <Download size={16} />
                  {t("coupons.importCoupons")}
                </Button>
                <Button $variant="outline">
                  <BarChart size={16} />
                  {t("coupons.viewReports")}
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          <div style={{ marginTop: 24 }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <select
                value={filterStoreId}
                onChange={(e) => setFilterStoreId(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", minWidth: 140, fontSize: 14 }}
              >
                <option value="">{t("coupons.allStores")}</option>
                {stores.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
              <div style={{ maxWidth: 400, flex: "1 1 280px" }}>
                <div style={{ position: "relative" }}>
                  <Search size={16} color="#6b7280" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                  <Input placeholder={t("coupons.searchCoupons")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ paddingLeft: 34 }} />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", minWidth: 160, fontSize: 14 }}
              >
                <option value="all">{t("coupons.allStatus")}</option>
                <option value="active">{t("coupons.active")}</option>
                <option value="inactive">{t("coupons.inactive")}</option>
                <option value="expired">{t("coupons.expired")}</option>
                <option value="scheduled">{t("coupons.scheduled")}</option>
                <option value="paused">{t("coupons.paused")}</option>
              </select>
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table>
<TableHeader>
                <TableRow>
                    <TableHead>{t("coupons.code")}</TableHead>
                    <TableHead>{t("coupons.store")}</TableHead>
                    <TableHead>{t("coupons.discount")}</TableHead>
                    <TableHead>{t("coupons.usage")}</TableHead>
                    <TableHead>{t("coupons.minMaxSpend")}</TableHead>
                    <TableHead>{t("coupons.expiryDate")}</TableHead>
                    <TableHead>{t("coupons.status")}</TableHead>
                    <TableHead style={{ width: 56, textAlign: "right" }}>{t("coupons.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && filteredCoupons.length === 0 ? (
                    <TableRow><TableCell colSpan={8} style={{ color: "#6b7280", padding: "32px" }}>{t("coupons.loading")}</TableCell></TableRow>
                  ) : error ? (
                    <TableRow><TableCell colSpan={8} style={{ color: "#dc2626", padding: "16px" }}>{error}</TableCell></TableRow>
                  ) : filteredCoupons.length === 0 ? (
                    <TableRow><TableCell colSpan={8} style={{ color: "#6b7280", padding: "32px", textAlign: "center" }}>{t("coupons.noCouponsCreate")}</TableCell></TableRow>
                  ) : (
                    filteredCoupons.map((coupon) => {
                      const cid = coupon._id || coupon.id;
                      const isOpen = openActionsId === cid;
                      return (
                        <TableRow key={cid}>
                          <TableCell style={{ fontWeight: 600 }}>{coupon.code}</TableCell>
                          <TableCell>{coupon.storeId?.name ?? coupon.store ?? "–"}</TableCell>
                          <TableCell>{formatAmount(coupon)}</TableCell>
                          <TableCell>
                            {coupon.usageCount ?? coupon.usedCount ?? 0} / {coupon.usageLimit ?? coupon.maxUsage ?? "∞"}
                          </TableCell>
                          <TableCell>{formatMinMaxSpend(coupon)}</TableCell>
                          <TableCell>{coupon.expiryDate || coupon.expiresAt || "–"}</TableCell>
                          <TableCell>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "9999px",
                                fontSize: "11px",
                                fontWeight: 700,
                                background: (coupon.status || coupon.isActive === true) === "active" || coupon.isActive ? "#dcfce7" : "#fef2f2",
                                color: (coupon.status || coupon.isActive) === "active" || coupon.isActive ? "#166534" : "#991b1b",
                                textTransform: "capitalize",
                              }}
                            >
                              {coupon.status ?? (coupon.isActive ? t("coupons.active") : t("coupons.inactive"))}
                            </span>
                          </TableCell>
                          <TableCell style={{ textAlign: "right", position: "relative" }} ref={isOpen ? actionsRef : null}>
                            <Button
                              $variant="ghost"
                              $size="icon"
                              style={{ minWidth: 32, height: 32 }}
                              onClick={(e) => { e.stopPropagation(); setOpenActionsId(isOpen ? null : cid); }}
                              aria-label="Actions"
                            >
                              <MoreVertical size={16} />
                            </Button>
                            {isOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: "100%",
                                  marginTop: 4,
                                  minWidth: 160,
                                  background: "#fff",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  zIndex: 10,
                                  padding: "4px 0",
                                }}
                              >
                                <button type="button" style={{ display: "block", width: "100%", padding: "8px 12px", textAlign: "left", border: "none", background: "none", fontSize: 14, cursor: "pointer" }} onClick={() => { setOpenActionsId(null); }}>{t("coupons.editCoupon")}</button>
                                <button type="button" style={{ display: "block", width: "100%", padding: "8px 12px", textAlign: "left", border: "none", background: "none", fontSize: 14, cursor: "pointer" }} onClick={() => { setOpenActionsId(null); }}>{t("coupons.viewAnalytics")}</button>
                                <button type="button" style={{ display: "block", width: "100%", padding: "8px 12px", textAlign: "left", border: "none", background: "none", fontSize: 14, cursor: "pointer" }} onClick={() => { setOpenActionsId(null); }}>{t("coupons.usageHistory")}</button>
                                <hr style={{ margin: "4px 0", border: "none", borderTop: "1px solid #e5e7eb" }} />
                                <button type="button" style={{ display: "block", width: "100%", padding: "8px 12px", textAlign: "left", border: "none", background: "none", fontSize: 14, cursor: "pointer", color: "#dc2626" }} onClick={() => { setOpenActionsId(null); dispatch(deleteCoupon(cid)); }}>{t("coupons.deactivate")}</button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <Card style={{ marginTop: 24, padding: "20px" }}>
            <p style={{ color: "#6b7280" }}>{t("coupons.analyticsPlaceholder")}</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Coupons;
